import { useRef, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import {
  Layout,
  LayoutContext,
  Header,
  Logo,
  Avatar,
  Button,
  Card,
  CardContent,
  HelpIcon,
  BellIcon,
  CheckIcon,
  TrashIcon,
  ContextualMenu,
  ContextualMenuItem,
  ContextualMenuContent,
  Divider,
  Popover,
  EmptyState,
  GlobalNavigation,
  SegmentedControl,
  SegmentedControlButton,
  SettingsIcon,
  SignOutIcon,
  SendIcon,
  AlertIcon,
  GraphIcon,
  LockIcon,
  GroupIcon,
  ColumnsIcon,
  DescriptionIcon,
} from '@forge/common';
import ProfilePage from './pages/profile/ProfilePage';
import FileManagementPage from './pages/file-management/FileManagementPage';
import AccessManagementPage from './pages/access-management/AccessManagementPage';
import AuditLogsPage from './pages/audit-logs/AuditLogsPage';
import ObservabilityPage from './pages/observability/ObservabilityPage';
import OracleDatabasesPage from './pages/observability/OracleDatabasesPage';
import AzureSqlDatabasesPage from './pages/observability/AzureSqlDatabasesPage';
import DatabasesPage from './pages/databases/DatabasesPage';
import IntakePage from './pages/intake/IntakePage';
import IntakeListPage from './pages/intake/IntakeListPage';
import IntakeNewPage from './pages/intake/IntakeNewPage';
import IntakeEditPage from './pages/intake/IntakeEditPage';
import IntakeJiraNewPage from './pages/intake/IntakeJiraNewPage';
import StatusDashboardPage from './pages/dashboard/StatusDashboardPage';
import PartnersPage from './pages/partner-exchange/PartnersPage';
import PartnerDetailPage from './pages/partner-exchange/PartnerDetailPage';
import ConnectionsPage from './pages/partner-exchange/ConnectionsPage';
import KeyringPage from './pages/partner-exchange/KeyringPage';
import JobsPage from './pages/partner-exchange/JobsPage';
import ActivityPage from './pages/partner-exchange/ActivityPage';
import SftpAccessPage from './pages/sftp-access/SftpAccessPage';
import { useRole } from './context/RoleContext';
import { UnderConstructionPage, NotFoundPage } from './pages/status';
import { useUnreadCount, useNotifications } from '@/hooks/useNotifications';
import { useApiMutation } from '@/hooks/useApiMutation';
import { markAllAsRead, markAsRead, deleteNotification } from '@/api/notifications';
import type { Notification } from '@/api/notifications';
import mockData from '@/data/mock-data.json';
import { getInitials, getAvatarColor, toTitleCase } from '@/utils/userDisplay';

const buildMenuConfig = (pathname: string, isAdmin: boolean) => [
  {
    title: 'Workspace',
    items: [
      {
        label: 'Dashboard',
        path: '/dashboard',
        prefix: <GraphIcon />,
        isActiveItem: pathname === '/' || pathname.startsWith('/dashboard'),
      },
      {
        label: 'File Management',
        path: '/file-management',
        prefix: <SendIcon />,
        isActiveItem: pathname === '/file-management',
      },
      {
        label: 'Intake',
        path: '/intake',
        prefix: <DescriptionIcon />,
        isActiveItem: pathname.startsWith('/intake'),
      },
      {
        label: 'Bug Reports',
        path: '/bug-reports',
        prefix: <AlertIcon />,
        isActiveItem: pathname === '/bug-reports',
      },
    ],
  },
  {
    title: 'Partner Exchange',
    items: [
      {
        label: 'Partners',
        path: '/partners',
        prefix: <GroupIcon />,
        isActiveItem: pathname.startsWith('/partners'),
      },
      {
        label: 'Connections',
        path: '/connections',
        prefix: <ColumnsIcon />,
        isActiveItem: pathname === '/connections',
      },
      {
        label: 'Keyring',
        path: '/keyring',
        prefix: <LockIcon />,
        isActiveItem: pathname === '/keyring',
      },
      {
        label: 'Jobs',
        path: '/jobs',
        prefix: <SendIcon />,
        isActiveItem: pathname === '/jobs',
      },
      {
        label: 'Activity',
        path: '/activity',
        prefix: <GraphIcon />,
        isActiveItem: pathname === '/activity',
      },
    ],
  },
  {
    title: 'Administration',
    items: [
      {
        label: 'Observability',
        path: '/observability',
        prefix: <GraphIcon />,
        isActiveItem: pathname === '/observability',
      },
      {
        label: 'Databases',
        path: '/databases',
        prefix: <ColumnsIcon />,
        isActiveItem: pathname === '/databases',
      },
      {
        label: 'Access Management',
        path: '/access-management',
        prefix: <GroupIcon />,
        isActiveItem: pathname === '/access-management',
      },
      {
        label: 'Audit Logs',
        path: '/audit-logs',
        prefix: <LockIcon />,
        isActiveItem: pathname === '/audit-logs',
      },
      ...(isAdmin
        ? [
            {
              label: 'SFTP Access',
              path: '/sftp-access',
              prefix: <LockIcon />,
              isActiveItem: pathname === '/sftp-access',
            },
          ]
        : []),
    ],
  },
];

const formatRelativeTime = (dateString: string): string => {
  const now = Date.now();
  const then = new Date(dateString).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
};

const NotificationItem = ({
  notification,
  onMarkRead,
  onDelete,
}: {
  notification: Notification;
  onMarkRead: (id: string) => void;
  onDelete: (id: string) => void;
}) => (
  <div
    className={`flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
      notification.read ? 'opacity-60' : ''
    }`}
    onClick={() => {
      if (!notification.read) onMarkRead(notification.id);
    }}
  >
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <p className={`text-sm truncate ${notification.read ? '' : 'font-semibold'}`}>
          {notification.title}
        </p>
        {!notification.read && (
          <span className="inline-block h-2 w-2 rounded-full bg-blue-500 shrink-0" />
        )}
      </div>
      {notification.message && (
        <p className="text-xs text-color-tertiary mt-0.5 line-clamp-2">
          {notification.message}
        </p>
      )}
      <p className="text-xs text-color-tertiary mt-1">
        {formatRelativeTime(notification.createdAt)}
      </p>
    </div>
    <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
      {!notification.read && (
        <Button
          aria-label="Mark as read"
          icon={<CheckIcon />}
          variant="quiet-primary"
          onClick={() => onMarkRead(notification.id)}
        />
      )}
      <Button
        aria-label="Delete notification"
        icon={<TrashIcon />}
        variant="quiet-primary"
        onClick={() => onDelete(notification.id)}
      />
    </div>
  </div>
);

const AppHeader = () => {
  const user = mockData.currentUser;
  const initials = getInitials(user.firstName, user.lastName, user.email);
  const avatarColor = getAvatarColor(user.email || `${user.firstName} ${user.lastName}`);
  const navigate = useNavigate();

  const avatarRef = useRef<HTMLDivElement>(null);
  const helpRef = useRef<HTMLButtonElement>(null);
  const notifRef = useRef<HTMLButtonElement>(null);

  const { count: unreadCount, reload: reloadCount } = useUnreadCount();
  const {
    notifications: fetchedNotifications,
    reload: reloadNotifications,
    isUsingMockData: isNotificationsMock,
  } = useNotifications({ limit: 20 });

  // Local copy so the dropdown stays interactive even when the API is
  // unavailable (mock mode). Seeded whenever fresh data arrives from the hook.
  const [items, setItems] = useState<Notification[]>([]);
  useEffect(() => {
    setItems(fetchedNotifications);
  }, [fetchedNotifications]);

  const localUnreadCount = useMemo(
    () => items.filter((n) => !n.read).length,
    [items],
  );
  const displayUnreadCount = isNotificationsMock ? localUnreadCount : unreadCount;

  const { mutate: doMarkAllRead } = useApiMutation({
    mutationFn: () => markAllAsRead(),
    successMessage: 'All notifications marked as read',
    onSuccess: () => {
      reloadCount();
      reloadNotifications();
    },
  });

  const { mutate: doMarkRead } = useApiMutation({
    mutationFn: (id: string) => markAsRead(id),
    onSuccess: () => {
      reloadCount();
      reloadNotifications();
    },
  });

  const { mutate: doDelete } = useApiMutation({
    mutationFn: (id: string) => deleteNotification(id),
    successMessage: 'Notification deleted',
    onSuccess: () => {
      reloadCount();
      reloadNotifications();
    },
  });

  const handleMarkRead = useCallback(
    (id: string) => {
      setItems((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
      );
      if (!isNotificationsMock) doMarkRead(id);
    },
    [doMarkRead, isNotificationsMock],
  );

  const handleMarkAllRead = useCallback(() => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    if (!isNotificationsMock) doMarkAllRead(undefined as never);
  }, [doMarkAllRead, isNotificationsMock]);

  const handleDelete = useCallback(
    (id: string) => {
      setItems((prev) => prev.filter((n) => n.id !== id));
      if (!isNotificationsMock) doDelete(id);
    },
    [doDelete, isNotificationsMock],
  );

  const [theme, setTheme] = useState(() =>
    document.documentElement.classList.contains('dark') ? 'dark' : 'light',
  );

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  return (
    <Header
      logo={
        <Logo
          icon
          iconVariant="color"
          interactive
          logoVariant="one-line"
          orientation="horizontal"
          suite="OneRing Internal Platform"
          suiteSuperScript=""
        />
      }
      rightSlot={
        <div className="grid auto-cols-auto grid-flow-col items-center gap-2">
          {/* Profile avatar */}
          <div ref={avatarRef}>
            <Avatar text={initials} aria-label={initials} interactive style={avatarColor} />
          </div>
          <ContextualMenu anchor={avatarRef} position="bottom-right">
            <ContextualMenuContent>
              <div>
                <p className="text-sm font-bold text-left">
                  {toTitleCase(user.firstName)} {toTitleCase(user.lastName)}
                </p>
                <p className="text-xs text-color-tertiary text-left">
                  {user.email}
                </p>
              </div>
            </ContextualMenuContent>
            <Divider />
            <ContextualMenuItem
              as="button"
              id="profile"
              label="Profile"
              prefix={<SettingsIcon />}
              onClick={() => navigate('/profile')}
            />
            <Divider />
            <ContextualMenuContent>
              <div className="flex items-center gap-3 px-3 py-1.5">
                <span className="text-sm">Theme</span>
                <SegmentedControl
                  id="theme-switcher"
                  size="small"
                  onChange={(e) => setTheme(e.target.value)}
                >
                  <SegmentedControlButton
                    id="theme-light"
                    value="light"
                    defaultChecked={theme === 'light'}
                  >
                    Light
                  </SegmentedControlButton>
                  <SegmentedControlButton
                    id="theme-dark"
                    value="dark"
                    defaultChecked={theme === 'dark'}
                  >
                    Dark
                  </SegmentedControlButton>
                </SegmentedControl>
              </div>
            </ContextualMenuContent>
            <Divider />
            <ContextualMenuItem
              as="button"
              id="signout"
              label="Signout"
              prefix={<SignOutIcon />}
              onClick={() => {}}
            />
          </ContextualMenu>

          {/* Help button */}
          <Button
            ref={helpRef}
            aria-label="Help"
            icon={<HelpIcon />}
            variant="tertiary"
          />
          <ContextualMenu anchor={helpRef} position="bottom-right">
            <ContextualMenuItem
              as="button"
              id="privacy"
              label="Privacy"
              onClick={() => {}}
            />
            <ContextualMenuItem
              as="button"
              id="cookies"
              label="Cookies"
              onClick={() => {}}
            />
            <ContextualMenuItem
              as="button"
              id="terms"
              label="Terms"
              onClick={() => {}}
            />
            <ContextualMenuItem
              as="button"
              id="help"
              label="Help"
              onClick={() => {}}
            />
            <ContextualMenuItem
              as="button"
              id="privacy-choices"
              label="Privacy choices"
              onClick={() => {}}
            />
          </ContextualMenu>

          {/* Notifications button */}
          <div className="relative">
            <Button
              ref={notifRef}
              aria-label="Notifications"
              icon={<BellIcon />}
              variant="tertiary"
            />
            {displayUnreadCount > 0 && (
              <span
                aria-label={`${displayUnreadCount} unread notifications`}
                className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-blue-500 pointer-events-none"
              />
            )}
          </div>
          <Popover
            anchor={notifRef}
            id="notifications-popover"
            position="bottom-right"
            fixed
          >
            <Card style={{ width: 'clamp(280px, 30vw, 400px)' }}>
              <CardContent>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold">Notifications</p>
                  {displayUnreadCount > 0 && (
                    <Button
                      variant="quiet-primary"
                      onClick={handleMarkAllRead}
                    >
                      Mark all as read
                    </Button>
                  )}
                </div>
                <Divider />
                {items.length === 0 ? (
                  <EmptyState
                    icon={<BellIcon />}
                    description="No notifications found"
                  />
                ) : (
                  <div className="max-h-80 overflow-y-auto -mx-4">
                    {items.map((n) => (
                      <NotificationItem
                        key={n.id}
                        notification={n}
                        onMarkRead={handleMarkRead}
                        onDelete={handleDelete}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </Popover>
        </div>
      }
    />
  );
};

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleSidebar } = useContext(LayoutContext);
  const { isAdmin } = useRole();

  return (
    <Layout
      header={<AppHeader />}
      leftSidebar={
        <GlobalNavigation
          animated
          menuConfig={buildMenuConfig(location.pathname, isAdmin)}
          suite="OneRing"
          suiteSuperScript=""
          onClose={() => toggleSidebar('left')}
          onChange={(path) => {
            navigate(path);
            toggleSidebar('left');
          }}
        />
      }
    >
      <Routes>
        <Route path="/" element={<StatusDashboardPage />} />
        <Route path="/dashboard" element={<StatusDashboardPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/file-management" element={<FileManagementPage />} />
        <Route path="/intake" element={<IntakeListPage />} />
        <Route path="/intake/new" element={<IntakeNewPage />} />
        <Route path="/intake/:id" element={<IntakePage />} />
        <Route path="/intake/:id/edit" element={<IntakeEditPage />} />
        <Route path="/intake/:id/jira/new" element={<IntakeJiraNewPage />} />
        <Route path="/access-management" element={<AccessManagementPage />} />
        <Route path="/bug-reports" element={<UnderConstructionPage />} />
        <Route path="/observability" element={<ObservabilityPage />} />
        <Route path="/observability/oracle" element={<OracleDatabasesPage />} />
        <Route path="/observability/azure-sql" element={<AzureSqlDatabasesPage />} />
        <Route path="/databases" element={<DatabasesPage />} />
        <Route path="/audit-logs" element={<AuditLogsPage />} />
        <Route path="/sftp-access" element={<SftpAccessPage />} />
        <Route path="/partners" element={<PartnersPage />} />
        <Route path="/partners/:id" element={<PartnerDetailPage />} />
        <Route path="/connections" element={<ConnectionsPage />} />
        <Route path="/keyring" element={<KeyringPage />} />
        <Route path="/jobs" element={<JobsPage />} />
        <Route path="/activity" element={<ActivityPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Layout>
  );
};

export default App;
